import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Spinner, Badge } from 'react-bootstrap';
import { useApi } from '../../contexts/ApiProvider';
import { useNavigate } from 'react-router-dom';

const TEMPLATES = [
    {
        key: 'fermentation',
        name: 'Fermentation & Brewing',
        description: 'Track batches, SCOBY lineage, and sensory taste tests.',
        icon: 'bi-droplet-half',
        color: 'primary'
    },
    {
        key: 'gardening',
        name: 'Container Gardening',
        description: 'Track seed inventory, pot dimensions, and plant growth phases.',
        icon: 'bi-flower1',
        color: 'success'
    },
    {
        key: 'leatherworking',
        name: 'Crafting & Fabrication',
        description: 'Track tool inventory, material costs, and project milestones.',
        icon: 'bi-hammer',
        color: 'warning'
    },
    {
        key: 'blank',
        name: 'Custom Tracking',
        description: 'Start with a blank canvas and define your own metrics.',
        icon: 'bi-stars',
        color: 'secondary'
    }
];

export default function HobbyDashboardContainer() {
    const api = useApi();
    const navigate = useNavigate();

    const [hobbies, setHobbies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState('fermentation');
    const [newHobby, setNewHobby] = useState({ name: '', description: '' });

    useEffect(() => {
        fetchHobbies();
        // eslint-disable-next-line
    }, []);

    const fetchHobbies = async () => {
        setIsLoading(true);
        const res = await api.get('/hobbies?per_page=100');
        if (res.ok) {
            setHobbies(res.body.items || []);
        }
        setIsLoading(false);
    };

    // Auto-fill the form name/description when a template is clicked
    const handleSelectTemplate = (template) => {
        setSelectedTemplate(template.key);
        setNewHobby({
            name: template.name,
            description: template.description
        });
    };

    const handleCreateHobby = async (e) => {
        e.preventDefault();
        const res = await api.post('/hobbies', {
            template_key: selectedTemplate,
            name: newHobby.name,
            description: newHobby.description
        });

        if (res.ok) {
            setShowCreateModal(false);
            fetchHobbies();
            // Optional: navigate immediately to the new hobby's dashboard
            // navigate(`/hobbies/${res.body.id}`);
        } else {
            alert("Failed to create hobby. Please try again.");
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return '';
        const d = new Date(isoString);
        return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <Container className="py-4 fade-in">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom flex-wrap gap-3">
                <div>
                    <h2 className="fw-bold mb-1">Hobbies & Projects</h2>
                    <p className="text-muted mb-0">Track your passions, inventory, and progress.</p>
                </div>
                <Button
                    variant="primary"
                    className="fw-bold rounded-pill px-4"
                    onClick={() => {
                        handleSelectTemplate(TEMPLATES[0]); // Default to first template
                        setShowCreateModal(true);
                    }}
                >
                    <i className="bi bi-plus-lg me-2"></i>New Hobby
                </Button>
            </div>

            {/* Hobby Grid */}
            {isLoading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : hobbies.length === 0 ? (
                <div className="text-center py-5 text-muted bg-light rounded-4 border">
                    <i className="bi bi-collection display-1 mb-3 opacity-25 d-block"></i>
                    <h4>No hobbies tracked yet.</h4>
                    <p>Select a template above to start tracking your first hobby.</p>
                </div>
            ) : (
                <Row className="g-4">
                    {hobbies.map(hobby => (
                        <Col md={6} lg={4} key={hobby.id}>
                            <Card
                                className="h-100 border-0 shadow-sm rounded-4 hover-scale pointer"
                                onClick={() => navigate(`/hobbies/${hobby.id}`)}
                            >
                                <Card.Body className="p-4 d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                                            <i className="bi bi-journal-bookmark-fill fs-5"></i>
                                        </div>
                                        <Badge bg="light" text="dark" className="border">
                                            Started {formatDate(hobby.created_at)}
                                        </Badge>
                                    </div>
                                    <h5 className="fw-bold text-dark mb-2">{hobby.name}</h5>
                                    <p className="text-muted small mb-4 flex-grow-1">
                                        {hobby.description || 'No description provided.'}
                                    </p>
                                    <div className="text-primary fw-bold small mt-auto border-top pt-3">
                                        View Dashboard <i className="bi bi-arrow-right ms-1"></i>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Create Hobby / Template Picker Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered size="lg" fullscreen="md-down">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">Start a New Hobby</Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4">
                    <p className="text-muted small mb-4">Choose a starting template. This will automatically set up the tracking fields, which you can customize later.</p>

                    <Row className="g-3 mb-4">
                        {TEMPLATES.map(template => (
                            <Col md={6} key={template.key}>
                                <div
                                    className={`p-3 rounded-4 border pointer transition-all h-100 ${selectedTemplate === template.key ? `border-${template.color} bg-${template.color} bg-opacity-10 shadow-sm` : 'bg-light border-light hover-bg-white'}`}
                                    onClick={() => handleSelectTemplate(template)}
                                >
                                    <div className="d-flex align-items-start gap-3">
                                        <div className={`text-${template.color} fs-3`}><i className={`bi ${template.icon}`}></i></div>
                                        <div>
                                            <h6 className="fw-bold mb-1">{template.name}</h6>
                                            <div className="small text-muted">{template.description}</div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>

                    <Form onSubmit={handleCreateHobby} className="border-top pt-4">
                        <h6 className="fw-bold mb-3">Hobby Details</h6>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold text-muted mb-1">Hobby Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={newHobby.name}
                                onChange={e => setNewHobby({...newHobby, name: e.target.value})}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label className="small fw-bold text-muted mb-1">Description (Optional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={newHobby.description}
                                onChange={e => setNewHobby({...newHobby, description: e.target.value})}
                            />
                        </Form.Group>
                        <Button variant="primary" type="submit" className="w-100 fw-bold rounded-pill py-2">
                            Initialize Tracker
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
}
